// ============================================================================
// Feature flags — per-client (per-owner) SaaS module toggles.
//
// The super-admin enables/disables whole modules per account from the admin
// panel (Manage Features). The app reads the current owner's enabled features
// to drive the dynamic nav, route guards, and API access. Flags are keyed to
// the account OWNER (see lib/tenant.js), so team members inherit the owner's
// modules. Mirrors the RBAC catalogue pattern in lib/permissions.js.
// ============================================================================
import { supabase } from './supabase';
import { getTenant } from './tenant';

// Static catalogue (must mirror the seeds in backend/feature_management.sql).
// `icon` is a lucide-react component name; consumers map it as needed.
export const FEATURE_CATALOG = [
  { key: 'billing',            name: 'Billing',             icon: 'FileText',      category: 'core' },
  { key: 'reparto',            name: 'Reparto',             icon: 'Truck',         category: 'core' },
  { key: 'reports',            name: 'Reports',             icon: 'BarChart3',     category: 'core' },
  { key: 'material_management', name: 'Material Management', icon: 'Boxes',         category: 'erp' },
  { key: 'production',         name: 'Production',          icon: 'Factory',       category: 'erp' },
  { key: 'inventory',          name: 'Inventory',           icon: 'Warehouse',     category: 'erp' },
  { key: 'employee',           name: 'Employee',            icon: 'Users',         category: 'erp' },
  { key: 'purchases',          name: 'Purchases',           icon: 'ShoppingCart',  category: 'erp' },
  { key: 'dispatch',           name: 'Dispatch',            icon: 'Send',          category: 'erp' },
  { key: 'customer_orders',    name: 'Customer Orders',     icon: 'ClipboardList', category: 'erp' },
  { key: 'quality_control',    name: 'Quality Control',     icon: 'ShieldCheck',   category: 'erp' },
  { key: 'fbr_invoicing',      name: 'FBR Digital Invoicing', icon: 'ScanLine',    category: 'fiscal' },
  { key: 'pra_invoicing',      name: 'PRA e-Invoicing (Punjab)', icon: 'ScanLine', category: 'fiscal' },
  { key: 'bank_sync',          name: 'Bank Sync (PSD2)',     icon: 'Landmark',      category: 'finance' },
  // The help chat itself is built in for every account; this flag only adds AI
  // answers for questions with no ready-made answer.
  { key: 'help_assistant',     name: 'Help Assistant — AI answers', icon: 'HelpCircle', category: 'general' },
  // The account's own mailbox inside zBilling (pages/Mail): IMAP over the email it connected.
  { key: 'mail_inbox',         name: 'Correo (buzón propio)', icon: 'Mail',        category: 'general' },
  // Mis Tiendas + the account's ZPOS shops (linked through zpos.zcom.es) and the
  // ZPOS+ Tiendas app's "Entrar con zBilling". The relay checks this same flag.
  { key: 'zpos_tiendas',       name: 'ZPOS Tiendas',          icon: 'Store',       category: 'general' },
  // Catálogos de productos (backend/product_catalogues.sql): button next to
  // Escanear in Productos, printable/PDF catalogues and a public link.
  { key: 'product_catalogue',  name: 'Catálogos de productos', icon: 'BookOpen',    category: 'general' },
];

// Per-owner cache so the nav/guards don't refetch on every mount. Keyed by
// owner id; cleared on logout / when the active account changes (impersonation).
let _cache = null; // { ownerId, keys: Set<string> }

export function clearFeatureCache() {
  _cache = null;
}

// Resolve the current account owner's ENABLED feature keys as a Set.
// RLS lets the owner and their team read their own client_features rows.
//
// Platform admins (super_admin/admin) get EVERY module, the same way
// permissionsForRole() gives them every permission: they are the people who turn
// modules on for other accounts, so a module they have not yet granted
// themselves must still be visible to them. Their own client_features rows are
// left untouched — this is a read-time bypass, not a grant.
export async function fetchClientFeatures() {
  const { ownerId, role } = await getTenant();
  if (!ownerId) return new Set();
  if (_cache && _cache.ownerId === ownerId) return _cache.keys;
  if (role === 'super_admin' || role === 'admin') {
    const keys = new Set(FEATURE_CATALOG.map((f) => f.key));
    _cache = { ownerId, keys };
    return keys;
  }
  const { data, error } = await supabase
    .from('client_features')
    .select('enabled, features!inner(feature_key)')
    .eq('owner_id', ownerId)
    .eq('enabled', true);
  if (error) {
    // Fail closed on gated modules but don't crash the app if the table/policy
    // isn't there yet — return an empty set so only ungated nav shows.
    console.warn('fetchClientFeatures failed:', error.message);
    return new Set();
  }
  const keys = new Set((data || []).map((r) => r.features?.feature_key).filter(Boolean));
  _cache = { ownerId, keys };
  return keys;
}
